import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@shared/ui/button'
import Input from '@shared/ui/input'
import { useToastStore } from '@shared/ui/toast/useToastStore'

import { useBonusManager } from '../lib/useBonusManager'

import styles from './BonusApplier.module.scss'

interface BonusApplierProps {
  onApplyBonuses?: (amount: number) => Promise<void>
  onClearBonuses?: () => Promise<void>
  disabled?: boolean
}

export default function BonusApplier ({
  onApplyBonuses,
  onClearBonuses,
  disabled = false
}: BonusApplierProps) {
  const { t: tca } = useTranslation('cart')
  const { t: tp } = useTranslation('personal')
  const { t: tc } = useTranslation('common')
  const addToast = useToastStore((state) => state.addToast)
  
  const bonusManager = useBonusManager()
  const {
    availableBonuses,
    appliedBonuses,
    hasAppliedBonuses,
    isBusy: bonusBusy
  } = bonusManager

  const [ inputValue, setInputValue ] = useState('')
  const [ isUseAllChecked, setIsUseAllChecked ] = useState(false)
  const [ isSubmitting, setIsSubmitting ] = useState(false)

  const isBusy = isSubmitting || bonusBusy

  // Sync input with applied amount
  useEffect(() => {
    if (hasAppliedBonuses && appliedBonuses > 0) {
      setInputValue(String(appliedBonuses))
    }
    else if (!inputValue) {
      setInputValue('')
    }
  }, [ hasAppliedBonuses, appliedBonuses, inputValue ])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '') // Only numbers
    const numValue = parseInt(value, 10) || 0
    
    if (numValue <= availableBonuses) {
      setInputValue(value)
      setIsUseAllChecked(numValue === availableBonuses)
    }
  }

  const handleInputBlur = () => {
    const numValue = parseInt(inputValue, 10) || 0
    if (numValue > availableBonuses) {
      setInputValue(String(availableBonuses))
      setIsUseAllChecked(true)
    }
  }

  const handleUseAllToggle = () => {
    const newUseAll = !isUseAllChecked
    setIsUseAllChecked(newUseAll)
    
    if (newUseAll) {
      setInputValue(String(availableBonuses))
    }
    else {
      setInputValue('')
    }
  }

  const handleApply = useCallback(async () => {
    const amount = parseInt(inputValue, 10) || 0
    
    if (amount <= 0) {
      addToast({
        message: tca('bonus.invalidAmount', 'Введите корректную сумму бонусов'),
        variant: 'error',
      })
      return
    }

    if (amount > availableBonuses) {
      addToast({
        message: tca('bonus.notEnough', 'Недостаточно бонусов'),
        variant: 'error',
      })
      return
    }

    setIsSubmitting(true)
    try {
      if (onApplyBonuses) {
        await onApplyBonuses(amount)
      }
      else {
        await bonusManager.applyBonuses(amount)
      }
      addToast({
        message: tca('bonus.applied', 'Списано {{count}} бонусов', { count: amount }),
        variant: 'success',
      })
    }
    catch (error) {
      console.error('Error applying bonuses:', error)
      addToast({
        message: tca('bonus.applyError', 'Ошибка при применении бонусов'),
        variant: 'error',
      })
    }
    finally {
      setIsSubmitting(false)
    }
  }, [ inputValue, availableBonuses, onApplyBonuses, bonusManager, addToast ])

  const handleClear = useCallback(async () => {
    setIsSubmitting(true)
    try {
      if (onClearBonuses) {
        await onClearBonuses()
      }
      else {
        await bonusManager.clearBonuses()
      }
      setInputValue('')
      setIsUseAllChecked(false)
      addToast({
        message: tca('bonus.cleared', 'Бонусы отменены'),
        variant: 'success',
      })
    }
    catch (error) {
      console.error('Error clearing bonuses:', error)
      addToast({
        message: tca('bonus.clearError', 'Ошибка при отмене бонусов'),
        variant: 'error',
      })
    }
    finally {
      setIsSubmitting(false)
    }
  }, [ onClearBonuses, bonusManager, addToast ])

  const formattedAvailableBonus = useMemo(() => {
    return new Intl.NumberFormat('ru-RU').format(availableBonuses)
  }, [ availableBonuses ])

  if (disabled || availableBonuses <= 0) {
    return (
      <div className={styles.root}>
        <div className={styles.message}>
          {availableBonuses <= 0 
            ? 'У вас нет доступных бонусов для списания'
            : 'Функция списания бонусов временно недоступна'
          }
        </div>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={`${styles.header} ${hasAppliedBonuses ? styles.headerApplied : ''}`}>
        <div className={styles.headerLeft}>
          <h3 className={styles.title}>
            {hasAppliedBonuses
              ? tca('bonus.writingOff', 'Списываем:')
              : `${tca('bonus.availableToUse')}:`}
          </h3>
          <div className={styles.bonusAmount}>
            <span>{hasAppliedBonuses ? appliedBonuses : formattedAvailableBonus} {tp('bonusHistory.bonusesAbbreviated')}</span>
            <span className={styles.tengeAmount}></span>
          </div>
        </div>

        {hasAppliedBonuses && (
          <Button
            onClick={handleClear}
            disabled={isBusy}
            theme="secondary"
            size="small"
          >
            {tc('cancel')}
          </Button>
        )}
      </div>

      {!hasAppliedBonuses && (
        <div className={styles.control}>
          <div className={styles.inputGroup}>
            <Input
              placeholder={tca('bonus.enterAmount')}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              disabled={isBusy}
              inputSize="large"
            />

            <input
              type="range"
              min={0}
              max={availableBonuses}
              value={inputValue === '' ? 0 : Number(inputValue)}
              onChange={(e) => setInputValue(e.target.value)}
              className={styles.slider}
              disabled={isBusy}
            />
          </div>

          <div className={styles.controls}>
            <label className={styles.useAllLabel}>
              <input
                type="checkbox"
                checked={isUseAllChecked}
                onChange={handleUseAllToggle}
                disabled={isBusy}
              />
              <span>{tca('bonus.useAll')}</span>
            </label>

            <div className={styles.buttons}>
              <Button
                onClick={handleApply}
                disabled={isBusy || !inputValue || parseInt(inputValue, 10) <= 0}
                loading={isBusy}
                size="small"
              >
                {tca('bonus.apply', 'Применить')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}